import type {
  AgentRole,
  AgentSessionStatus,
  HumanTaskType,
  KbCategoryCode,
  KbVersionStatus,
} from '@paguz/contracts';
import type { BadgeProps } from '@paguz/ui';

type Tone = NonNullable<BadgeProps['variant']>;

export const AGENT_LABEL: Readonly<Record<AgentRole, string>> = {
  ORIGINATION_ADVISOR: 'Asesor IA (originación)',
  PURCHASE_ASSISTANT: 'Agente de Compras',
  ANALYST_COPILOT: 'Copiloto del Analista',
  DOCUMENT_READER: 'Lector de documentos',
  CASH_FLOW_ANALYST: 'Analista de flujo de caja',
  DECISION_MEMO: 'Memo de decisión',
  MEMORY_EXTRACTOR: 'Extractor de memoria',
  SUMMARIZER: 'Resumidor',
  INPUT_GUARD: 'Guarda de entrada',
  EVAL_JUDGE: 'Juez de evaluaciones',
};

/** Agentes conversacionales con kill switch global (`AgentSettings.enabled`). */
export const CONVERSATIONAL_AGENTS = [
  'ORIGINATION_ADVISOR',
  'PURCHASE_ASSISTANT',
  'ANALYST_COPILOT',
] as const satisfies readonly AgentRole[];

export const SESSION_STATUS: Readonly<Record<AgentSessionStatus, { label: string; tone: Tone }>> = {
  ACTIVE: { label: 'Activa', tone: 'success' },
  WAITING_USER: { label: 'Esperando al cliente', tone: 'secondary' },
  WAITING_HITL: { label: 'Esperando humano', tone: 'warning' },
  TAKEN_OVER: { label: 'Control humano', tone: 'highlight' },
  INTERRUPTED: { label: 'Interrumpida', tone: 'warning' },
  CLOSED: { label: 'Cerrada', tone: 'outline' },
};

export const TASK_TYPE: Readonly<Record<HumanTaskType, string>> = {
  CREDIT_DECISION: 'Decisión de crédito',
  DOCUMENT_VALIDATION: 'Validar documento',
  SESSION_TAKEOVER: 'Atender conversación',
  DISPUTE_DECISION: 'Decidir reclamo',
  EXCEPTION_REVIEW: 'Revisar excepción',
  DISPUTE_OVERDUE: 'Reclamo vencido',
  STUCK_PENDING: 'Compra sin confirmar',
};

export const TASK_PRIORITY: Readonly<Record<string, { label: string; tone: Tone }>> = {
  LOW: { label: 'Baja', tone: 'outline' },
  NORMAL: { label: 'Normal', tone: 'secondary' },
  HIGH: { label: 'Alta', tone: 'warning' },
  URGENT: { label: 'Urgente', tone: 'danger' },
};

export const TASK_STATUS: Readonly<Record<string, { label: string; tone: Tone }>> = {
  OPEN: { label: 'Abierta', tone: 'highlight' },
  ASSIGNED: { label: 'Asignada', tone: 'secondary' },
  IN_PROGRESS: { label: 'En curso', tone: 'secondary' },
  RESOLVED: { label: 'Resuelta', tone: 'success' },
  CANCELLED: { label: 'Cancelada', tone: 'outline' },
};

export const KB_CATEGORY: Readonly<Record<KbCategoryCode, string>> = {
  LINEAMIENTOS: 'Lineamientos de la entidad',
  PROCESO_ORIGINACION: 'Proceso de originación',
  PRODUCTO: 'Producto',
  REQUISITOS: 'Requisitos documentales',
  MARGENES_SECTOR: 'Márgenes por sector',
  FAQ: 'Preguntas frecuentes',
  EDUCACION_FINANCIERA: 'Educación financiera',
  POLITICA_PAGOS: 'Política de pagos',
  POLITICA_DISPUTAS: 'Política de disputas',
};

export const KB_AUDIENCE: Readonly<Record<string, string>> = {
  AGENT: 'Solo el agente',
  CUSTOMER: 'Cliente',
  BOTH: 'Cliente y agente',
};

export const KB_VERSION_STATUS: Readonly<Record<KbVersionStatus, { label: string; tone: Tone }>> = {
  DRAFT: { label: 'Borrador', tone: 'secondary' },
  IN_REVIEW: { label: 'En revisión', tone: 'warning' },
  PUBLISHED: { label: 'Publicada', tone: 'success' },
  ARCHIVED: { label: 'Archivada', tone: 'outline' },
};

export const GUARD_OUTCOME: Readonly<Record<string, Tone>> = {
  PASS: 'success',
  MODIFIED: 'warning',
  BLOCKED: 'danger',
};
